import { MutationCache, QueryCache, QueryClient, type QueryKey } from '@tanstack/react-query'
import { toast, toastApiError } from '@/lib/toast'
import { asApiError, type ApiError } from './errors'

declare module '@tanstack/react-query' {
  interface Register {
    defaultError: ApiError
    mutationMeta: {
      /** Query keys (prefixes) to invalidate after the mutation succeeds. */
      invalidates?: QueryKey[]
      /** Set false when the caller shows the error inline (for example form field errors). */
      errorToast?: boolean
      /** Optional success toast text. */
      successMessage?: string
    }
    queryMeta: {
      /** Show a toast when a background refetch fails (initial load errors render inline). */
      errorToast?: boolean
    }
  }
}

function shouldRetry(failureCount: number, error: unknown): boolean {
  const e = asApiError(error)
  // Retry network failures and 5xx; never retry 4xx (the answer won't change).
  if (e.status === 0 || e.status >= 500) return failureCount < 2
  return false
}

export function createQueryClient(): QueryClient {
  const client: QueryClient = new QueryClient({
    queryCache: new QueryCache({
      onError(error, query) {
        // Initial-load failures render inline via <QueryState/>. Only toast when
        // data was already on screen and a refetch failed, or the query opts in.
        if (query.meta?.errorToast || query.state.data !== undefined) toastApiError(error)
      },
    }),
    mutationCache: new MutationCache({
      onError(error, _vars, _ctx, mutation) {
        if (mutation.meta?.errorToast !== false) toastApiError(error)
      },
      async onSuccess(_data, _vars, _ctx, mutation) {
        if (mutation.meta?.successMessage) toast.success(mutation.meta.successMessage)
        const keys = mutation.meta?.invalidates ?? []
        await Promise.all(keys.map((queryKey) => client.invalidateQueries({ queryKey })))
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        retry: shouldRetry,
        retryDelay: (n) => Math.min(1000 * 2 ** n, 8000),
        refetchOnWindowFocus: true,
      },
      mutations: { retry: false },
    },
  })
  return client
}
